import { AttemptStatus } from '@/types/attempt';
import { createFileRoute } from '@tanstack/react-router';
import PageGray from '../../components/General/PageGray';
import H1 from '../../components/Typography/Headings/H1';
import P from '../../components/Typography/P';
import LastSimulation from '../../components/Dashboard/LastSimulation';
import InProgressBox from '../../components/Dashboard/InProgressBox';

import DashQuestionsStats from '../../components/Dashboard/DashQuestionsStats';
import Diagnosis from '../../components/Stats/Diagnosis';
import { useGetAttempts } from '../../hooks/attempts/useAttempts';
import { useGetCollection } from '../../hooks/collections/useCollections';
import Loader from '../../components/Loader/Loader';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetStats } from '../../hooks/profile/useStats';

export const Route = createFileRoute('/_sidebar/')({
  component: Dashboard,
});

function Dashboard() {
  const { t } = useTranslation();
  const {
    attempts,
    isFetching: isAttemptsFetching,
    hasNextPage: attemptsHasNextPage,
    fetchNextPage: attemptsFetchNextPage,
    isFetchingNextPage: attemptsIsFetchingNextPage,
  } = useGetAttempts();

  const { brandStats: stats } = useGetStats();

  useEffect(() => {
    if (attemptsHasNextPage && !attemptsIsFetchingNextPage) {
      attemptsFetchNextPage();
    }
  }, [attemptsHasNextPage, attemptsIsFetchingNextPage]);

  const completedAttempts = attempts.filter(
    (a) => a.status === AttemptStatus.COMPLETED,
  );
  const inProgressAttempts = attempts.filter(
    (a) => a.status === AttemptStatus.IN_PROGRESS,
  );

  const hasDoneSimulations = (stats?.overall?.completedSimulations || 0) + (stats?.overall?.completedTrainingSessions || 0) > 0;

  const lastSimulation = hasDoneSimulations
    ? [...completedAttempts].sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      )[0]
    : undefined;

  const lastInProgress = inProgressAttempts[0];

  const { collection: lastInProgressCollection } = useGetCollection(lastInProgress?.collectionId ?? '');

  if (isAttemptsFetching && attempts.length === 0) {
    return <Loader fullScreen />;
  }

  return (
    <PageGray className="flex flex-col gap-8">
      <div className="flex flex-col gap-4">
        <H1>{t('dashboard.title')}</H1>
        <P>{t('dashboard.subtitle')}</P>
      </div>
      <div className="flex flex-col gap-6">
        <div className="flex items-center lg:flex-row flex-col gap-6">
          <div
            className={
              hasDoneSimulations
                ? 'w-full lg:w-[65%] h-full'
                : 'lg:w-full w-full h-full'
            }
          >
            <InProgressBox
              lastSimulation={lastInProgress}
              hasDoneSimulations={hasDoneSimulations}
              inProgressAttempts={inProgressAttempts}
              lastSimulationName={lastInProgressCollection?.name}
            />
          </div>
          {hasDoneSimulations && lastSimulation && (
            <div className="lg:w-[35%] w-full h-full">
              <LastSimulation lastSimulation={lastSimulation} />
            </div>
          )}
        </div>
        {stats && (
          <DashQuestionsStats
            stats={stats}
            hasDoneSimulations={hasDoneSimulations}
          />
        )}
      </div>
      <Diagnosis />
    </PageGray>
  );
}
